import axios, { AxiosInstance } from 'axios';
import { 
  PatientInquiry, 
  AnalyticsData, 
  ModerationQueueItem,
  ComplianceTag,
  ComplianceTagsResponse,
  ComplianceTagDetailResponse,
  ReviewSubmission,
  CreateTagRequest,
  ThresholdReviewRequest,
  ThresholdReviewRequestsResponse,
  ThresholdRequestResolution,
} from '../types';

const API_URL = process.env.REACT_APP_FUNCTION_API_URL || 'https://famelia-demo-fn-b1.azurewebsites.net/api';

// Auth Bridge for Healthie account creation
const BRIDGE_URL = process.env.REACT_APP_BRIDGE_URL || 'https://famelia-healthie-auth-bridge.azurewebsites.net';
const ADMIN_API_KEY = process.env.REACT_APP_ADMIN_API_KEY || '';

export interface CreateHealthieAccountResponse {
  success: boolean;
  healthie_patient?: {
    id: string;
    set_password_link: string;
  };
  inquiry_updated?: boolean;
  account_linked?: boolean;
  error?: string;
}

class AdminApiService {
  private client: AxiosInstance;

  constructor() {
    // Single client for B1 Function App
    this.client = axios.create({
      baseURL: API_URL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  setAuthToken(token: string) {
    this.client.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }

  getAuthToken(): string {
    return (this.client.defaults.headers.common['Authorization'] as string) ?? '';
  }

  async getPatientInquiries(includeCardUrls = false): Promise<PatientInquiry[]> {
    const response = await this.client.get('/patient-inquiries-admin', {
      params: includeCardUrls ? { include_card_urls: '1' } : undefined,
    });
    return response.data.data || [];
  }

  async getAnalytics(): Promise<AnalyticsData> {
    const response = await this.client.get('/admin/data', {
      params: { type: 'analytics' }
    });
    return response.data.data;
  }

  async updatePatientInquiry(inquiry: Partial<PatientInquiry>): Promise<void> {
    await this.client.post('/update-patient-inquiry', inquiry);
  }

  async createHealthieAccount(inquiry: PatientInquiry, dietitianId: string): Promise<CreateHealthieAccountResponse> {
    const nameParts = inquiry.full_name.trim().split(/\s+/);
    const firstName = nameParts[0] || '';
    const lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';

    const response = await axios.post(`${BRIDGE_URL}/api/createstubpatient`, {
      patient: {
        first_name: firstName,
        last_name: lastName,
        email: inquiry.email,
        dob: inquiry.dob || undefined,
        gender: inquiry.gender || undefined,
        phone_number: inquiry.phone || undefined,
      },
      dietitian_id: dietitianId,
      health_data: {
        height_inches: inquiry.height_inches,
        weight_lbs: inquiry.weight_lbs,
        goals: inquiry.goals_detail,
      },
      inquiry_id: inquiry.id,
      famelia_user_id: inquiry.famelia_user_id || null,
    }, {
      headers: {
        'Content-Type': 'application/json',
        'X-Admin-Key': ADMIN_API_KEY,
      },
      timeout: 60000,
    });

    return response.data;
  }

  async getModerationQueue(): Promise<ModerationQueueItem[]> {
    const response = await this.client.get('/moderation-queue-admin');
    return response.data.data || [];
  }

  async approveModerationItem(id: string): Promise<void> {
    await this.client.post(`/admin/moderation-queue/${id}/approve`);
  }

  async rejectModerationItem(id: string): Promise<void> {
    await this.client.post(`/admin/moderation-queue/${id}/reject`);
  }

  async getUserReports(status: string = 'pending'): Promise<any> {
    const response = await this.client.get('/user-reports-admin', {
      params: { status: status === 'all' ? undefined : status }
    });
    return response.data.data || [];
  }

  async getUserReport(id: string): Promise<any> {
    const response = await this.client.get(`/user-reports-admin/${id}`);
    return response.data;
  }

  async dismissReport(id: string, adminNotes: string): Promise<void> {
    // No-op for now - Express API crashed
  }

  async takeActionOnReport(id: string, adminNotes: string): Promise<void> {
    await this.client.patch(`/user-reports-admin/${id}/action`, {
      admin_notes: adminNotes
    });
  }

  async getAccountDeletions(status: string = 'all'): Promise<any[]> {
    const response = await this.client.get('/account-deletions-admin', {
      params: { status: status === 'all' ? undefined : status }
    });
    return response.data.data || [];
  }

  async updateAccountDeletion(request: any): Promise<void> {
    await this.client.patch(`/account-deletions-admin/${request.id}`, request);
  }

  // ============================================
  // COMPLIANCE REVIEW METHODS
  // ============================================

  async getComplianceTags(params?: {
    status?: string;
    category?: string;
    is_system?: string;
    search?: string;
  }): Promise<ComplianceTagsResponse> {
    const response = await this.client.get('/compliance-tags-admin', { params });
    return response.data.data;
  }

  async getComplianceTagDetail(ncmTagId: string): Promise<ComplianceTagDetailResponse> {
    const response = await this.client.get(`/compliance-tags-admin/${encodeURIComponent(ncmTagId)}`);
    return response.data.data;
  }

  async updateComplianceTag(ncmTagId: string, updates: ReviewSubmission): Promise<ComplianceTag> {
    const response = await this.client.patch(
      `/compliance-tags-admin/${encodeURIComponent(ncmTagId)}`,
      updates
    );
    return response.data.data.tag;
  }

  async promoteComplianceTag(ncmTagId: string, reviewNotes: string): Promise<ComplianceTag> {
    const response = await this.client.post(
      `/compliance-tags-admin/${encodeURIComponent(ncmTagId)}/promote`,
      { review_notes: reviewNotes }
    );
    return response.data.data.tag;
  }

  async createComplianceTag(tagData: CreateTagRequest): Promise<ComplianceTag> {
    const response = await this.client.post('/compliance-tags-admin', tagData);
    return response.data.data.tag;
  }

  async deleteComplianceTag(ncmTagId: string): Promise<{ deleted: boolean; cache_invalidated: number }> {
    const response = await this.client.delete(`/compliance-tags-admin/${encodeURIComponent(ncmTagId)}/delete`);
    return response.data.data;
  }

  // ============================================
  // RECIPE MANAGER METHODS
  // ============================================

  async getRecipes(params?: {
    is_public?: string;
    author_id?: string;
    page?: number;
    limit?: number;
  }): Promise<{ data: any[]; count: number; page: number; limit: number }> {
    const response = await this.client.get('/recipe-manager-list', { params });
    return response.data;
  }

  async searchRecipes(urls: string[]): Promise<{ results: any[]; scraped: number; total: number }> {
    const response = await this.client.post('/recipe-manager-search', { urls }, { timeout: 300000 });
    return response.data;
  }

  async importRecipes(recipes: any[], authorUserId: string): Promise<{ imported: any[]; errors: any[]; importedCount: number; errorCount: number }> {
    const response = await this.client.post('/recipe-manager-import', { recipes, author_user_id: authorUserId }, { timeout: 300000 });
    return response.data;
  }

  async publishRecipes(ids: string[], isPublic: boolean): Promise<{ updated: number }> {
    const response = await this.client.patch('/recipe-manager-publish', { ids, is_public: isPublic });
    return response.data;
  }

  async deleteRecipes(ids: string[]): Promise<{ deleted: number }> {
    const response = await this.client.delete('/recipe-manager-delete', { data: { ids } });
    return response.data;
  }

  async chatRecipes(message: string): Promise<any> {
    const response = await this.client.post('/recipe-manager-chat', { message }, { timeout: 300000 });
    return response.data;
  }

  async updateRecipe(id: string, fields: Record<string, any>): Promise<{ updated: string }> {
    const response = await this.client.patch('/recipe-manager-update', { id, ...fields });
    return response.data;
  }

  async getRecipeDetail(id: string): Promise<any> {
    const response = await this.client.get('/recipe-manager-detail', { params: { id } });
    return response.data;
  }

  async uploadRecipeMedia(recipeId: string, file: File, isPrimary: boolean = false): Promise<any> {
    const response = await this.client.post(
      `/recipe-manager-media?recipe_id=${recipeId}&is_primary=${isPrimary}`,
      file,
      { headers: { 'Content-Type': file.type }, timeout: 120000 },
    );
    return response.data;
  }

  async deleteRecipeMedia(mediaAssetId: string): Promise<any> {
    const response = await this.client.delete('/recipe-manager-media', { data: { media_asset_id: mediaAssetId } });
    return response.data;
  }

  async extractTimers(recipeId: string): Promise<any> {
    const response = await this.client.post(
      `/recipe-manager-extract-timers?recipe_id=${recipeId}`,
      {},
      { timeout: 60000 },
    );
    return response.data;
  }

  async saveAIImage(recipeId: string, blobPath: string, isPrimary = true): Promise<any> {
    const response = await this.client.post('/recipe-manager-save-image', {
      recipe_id: recipeId,
      blob_path: blobPath,
      is_primary: isPrimary,
    });
    return response.data;
  }

  async getIngredientNutrition(ingredient: string): Promise<any> {
    const response = await this.client.get('/recipe-manager-ingredient-nutrition', { params: { ingredient }, timeout: 30000 });
    return response.data;
  }

  async generateAIImage(recipeId: string): Promise<any> {
    const response = await this.client.post(
      `/recipe-manager-generate-image?recipe_id=${recipeId}`,
      {},
      { timeout: 120000 },
    );
    return response.data;
  }

  async backfillFavicons(): Promise<{ success: boolean; updated: number; total?: number; message?: string }> {
    const response = await this.client.post('/recipe-manager-backfill-favicons', {}, { timeout: 60000 });
    return response.data;
  }

  // ============================================
  // THRESHOLD REVIEW REQUEST METHODS
  // ============================================

  async getThresholdReviewRequests(params?: {
    status?: string;
    ncm_tag_id?: string;
    limit?: number;
    offset?: number;
  }): Promise<ThresholdReviewRequestsResponse> {
    const response = await this.client.get('/threshold-review-requests', { params });
    return response.data.data;
  }

  async resolveThresholdRequest(
    requestId: string, 
    resolution: ThresholdRequestResolution
  ): Promise<ThresholdReviewRequest> {
    const response = await this.client.patch(
      `/threshold-review-requests/${requestId}`,
      resolution
    );
    return response.data.data.request;
  }

  // ============================================
  // PUSH NOTIFICATIONS
  // ============================================

  async getNotificationLogs(params?: {
    limit?: number;
    offset?: number;
    userId?: string;
    channel?: string;
    type?: string;
  }): Promise<{ logs: any[]; total: number }> {
    const response = await this.client.get('/push-notifications-admin', {
      params: { action: 'logs', ...params },
    });
    return response.data;
  }

  async getNotificationTokens(params?: {
    limit?: number;
    offset?: number;
    search?: string;
  }): Promise<{ tokens: any[]; total: number }> {
    const response = await this.client.get('/push-notifications-admin', {
      params: { action: 'tokens', ...params },
    });
    return response.data;
  }

  async getNotificationStats(): Promise<{ stats: any }> {
    const response = await this.client.get('/push-notifications-admin', {
      params: { action: 'stats' },
    });
    return response.data;
  }

  async getSchedulerConfig(): Promise<{ config: any; defaults: any }> {
    const response = await this.client.get('/push-notifications-admin', {
      params: { action: 'config' },
    });
    return response.data;
  }

  async updateSchedulerConfig(config: any): Promise<{ success: boolean; config: any }> {
    const response = await this.client.post('/push-notifications-admin?action=updateConfig', { config });
    return response.data;
  }

  async sendTestPush(payload: {
    userId?: string;
    userIds?: string[];
    type: string;
    title: string;
    body: string;
    data?: Record<string, string>;
    skipRateLimit?: boolean;
  }): Promise<{ success: boolean; sent: number; skipped: number; skipReasons?: string[]; errors?: string[] }> {
    const response = await this.client.post('/send-push-notification', payload);
    return response.data;
  }

  // ============================================
  // CHALLENGES & POINT RULES
  // ============================================

  async getChallenges(): Promise<any[]> {
    const response = await this.client.get('/challenge-admin');
    return response.data.challenges || [];
  }

  async createChallenge(challenge: any): Promise<any> {
    const response = await this.client.post('/challenge-admin', challenge);
    return response.data.challenge;
  }

  async updateChallenge(id: string, updates: any): Promise<any> {
    const response = await this.client.patch(`/challenge-admin/${id}`, updates);
    return response.data.challenge;
  }

  async deleteChallenge(id: string): Promise<void> {
    await this.client.delete(`/challenge-admin/${id}`);
  }

  async getChallengeLeaderboard(id: string): Promise<any[]> {
    const response = await this.client.get(`/challenge-admin/${id}/leaderboard`);
    return response.data.leaderboard || [];
  }

  async getPointRules(): Promise<any[]> {
    const response = await this.client.get('/point-rules-admin');
    return response.data.rules || [];
  }

  async updatePointRule(id: string, updates: any): Promise<any> {
    const response = await this.client.patch(`/point-rules-admin/${id}`, updates);
    return response.data.rule;
  }

  // ============================================
  // PERSONAL CHALLENGES
  // ============================================

  async getPersonalChallenges(patientId?: string): Promise<any[]> {
    const params = patientId ? `?patient_id=${patientId}` : '';
    const response = await this.client.get(`/personal-challenges${params}`);
    return response.data.challenges || [];
  }

  async createPersonalChallenge(data: any): Promise<any> {
    const response = await this.client.post('/personal-challenges', data);
    return response.data.challenge;
  }
}

export const adminApi = new AdminApiService();

// Export individual functions for convenience
export const getUserReports = (status: string) => adminApi.getUserReports(status);
export const getUserReport = (id: string) => adminApi.getUserReport(id);
export const dismissReport = (id: string, notes: string) => adminApi.dismissReport(id, notes);
export const takeActionOnReport = (id: string, notes: string) => adminApi.takeActionOnReport(id, notes);

